import { useContentfulInspectorMode } from '@contentful/live-preview/react'
import DefaultComponent from '@entries/default-component'
import { useEntryInspector } from '@hooks/use-entry-inspector'
import { ErrorBoundary } from '@ui/error-boundary'
import React, {
  type ElementType,
  type FC,
  forwardRef,
  memo,
  type PropsWithChildren,
} from 'react'

type Props<T = Queries.EntryFragment> = {
  content: T
  mapping?: Record<string, ElementType>
  mappingKeyResolver?: (content: T) => string
  className?: string
}

type EntryFC<T = Queries.EntryFragment> = FC<PropsWithChildren<Props<T>>>

export const Entry: EntryFC = memo(
  forwardRef(
    (
      { content, mapping = {}, className, children, mappingKeyResolver },
      ref
    ) => {
      const Component = mappingKeyResolver
        ? mapping[mappingKeyResolver(content)] || DefaultComponent
        : mapping[content?.__typename] || DefaultComponent

      const entryInspectorProps = useEntryInspector({
        contentTemplateId: Component?.displayName,
        contentTypeId: content?.__typename,
        entryId:
          content?.contentful_id ||
          content?.netreviewId ||
          content?.trustpilotId,
      })

      const contentfulInspectorProps = useContentfulInspectorMode({
        entryId: content?.contentful_id,
      })

      if (!content) {
        return null
      }

      return (
        <ErrorBoundary>
          <Component
            ref={ref}
            className={className}
            content={content}
            entryInspectorProps={entryInspectorProps}
            fieldInspectorProps={contentfulInspectorProps}
          >
            {children}
          </Component>
        </ErrorBoundary>
      )
    }
  ),
  // Only re-render if content and children changes, ignore mapping on second re-render
  // mapping is a determinist immutable array
  (oldProps, newProps) =>
    Object.is(oldProps.className, newProps.className) &&
    Object.is(oldProps.content, newProps.content) &&
    Object.is(oldProps.mappingKeyResolver, newProps.mappingKeyResolver) &&
    Object.is(oldProps.children, newProps.children)
)

Entry.displayName = 'Entry'
